import { useCallback, useState } from 'react';
import { useCollapseAnimation } from './useCollapseAnimation';

/**
 * useCollapse 选项配置。
 *
 * - 可支持受控/非受控两种折叠模式；
 * - 可选接入高度动画（依赖 contentRef 与 duration/enabled）；
 * - 设计为通用 Hook，可在任何折叠类组件中复用（不仅限于 CollapseCard）。
 */
export interface IUseCollapseOptions {
  collapsed?: boolean;
  defaultCollapsed?: boolean;
  onCollapseChange?: (collapsed: boolean) => void;
  /** 需要计算高度的内容容器 ref，一般为折叠区域的根元素 */
  contentRef?: React.RefObject<HTMLDivElement | null>;
  animationDuration?: number;
  enableAnimation?: boolean;
}

/**
 * useCollapse 返回结果。
 *
 * - collapsed：当前折叠状态；
 * - maxHeight：用于动画的最大高度（number 为像素值，'none' 表示不限制）；
 * - isAnimating：当前是否处于高度动画过程。
 */
export interface IUseCollapseReturn {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean | ((prev: boolean) => boolean)) => void;
  toggleCollapsed: () => void;
  maxHeight: number | 'none';
  isAnimating: boolean;
}

/**
 * 通用折叠状态管理 Hook。
 *
 * 用途：
 * - 为 CollapseCard 等组件提供统一的折叠/展开状态管理；
 * - 封装受控/非受控逻辑，并统一触发 onCollapseChange 回调；
 * - 可选启用高度动画，避免重复实现相同的动画逻辑。
 *
 * 复用建议：
 * - 任何需要「点击标题区切换内容显隐」的组件，都可以直接使用本 Hook；
 * - 组件内部将折叠区域根元素挂载到 contentRef，并使用返回的
 *   collapsed / toggleCollapsed / maxHeight / isAnimating 控制 UI 即可。
 */
export function useCollapse(options: IUseCollapseOptions = {}): IUseCollapseReturn {
  const {
    collapsed: controlledCollapsed,
    defaultCollapsed = false,
    onCollapseChange,
    contentRef,
    animationDuration = 300,
    enableAnimation = true,
  } = options;

  const isControlled = controlledCollapsed !== undefined;
  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);

  const collapsed = isControlled ? (controlledCollapsed as boolean) : internalCollapsed;

  const setCollapsed = useCallback(
    (next: boolean | ((prev: boolean) => boolean)) => {
      // 关键点：
      // - 非受控场景下，若 next 是函数，则必须基于 React 内部的 prev 计算，
      //   否则在“同一次 act 内快速连点多次”时会因为闭包滞后导致回调顺序/最终状态错误。
      if (typeof next === 'function') {
        if (!isControlled) {
          setInternalCollapsed((prev) => {
            const nextCollapsed = (next as (prev: boolean) => boolean)(prev);
            onCollapseChange?.(nextCollapsed);
            return nextCollapsed;
          });
          return;
        }

        // 受控场景：状态由外部决定，只能基于当前受控值计算下一状态用于通知。
        const nextCollapsed = (next as (prev: boolean) => boolean)(collapsed);
        onCollapseChange?.(nextCollapsed);
        return;
      }

      if (!isControlled) {
        setInternalCollapsed(next);
      }
      onCollapseChange?.(next);
    },
    [collapsed, isControlled, onCollapseChange]
  );

  const toggleCollapsed = useCallback(() => {
    // 使用函数式更新避免依赖闭包中的 collapsed，减少并发/连续触发时的状态竞争风险。
    // 该方式虽然会让 toggleCollapsed 的语义更稳健，但 setCollapsed 回调内部仍依赖 collapsed，
    // 因而 setCollapsed 引用可能随渲染变化；不过更新/回调的业务结果不变。
    setCollapsed((prev) => !prev);
  }, [setCollapsed]);

  const { maxHeight, isAnimating } = useCollapseAnimation({
    collapsed,
    contentRef,
    duration: animationDuration,
    enabled: enableAnimation && !!contentRef,
  });

  return {
    collapsed,
    setCollapsed,
    toggleCollapsed,
    maxHeight,
    isAnimating,
  };
}

